/**
 * @fileoverview CDN helper for the public Supabase Storage bucket `public-cdn`.
 *
 * The bucket is public; assets are served directly via Storage's
 * `/storage/v1/object/public/<bucket>/<key>` URL without authentication.
 *
 * Bucket layout:
 *   - covers/<file>
 *   - guides/<file>
 *   - landing-page-previews/<file>
 *   - not-found/<file>
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const BUCKET = "public-cdn";

const CDN_BASE = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}`;

import { getCdnVersion } from "@/lib/cdnVersions";

/**
 * Build a public URL for a key inside the `public-cdn` bucket.
 * Leading slashes in `key` are tolerated.
 *
 * If a version is known for the key (populated by `CdnVersionProvider`), it is
 * appended as `?v=<version>` so changes propagate past browser/CDN caches.
 */
export function cdn(key: string): string {
  const cleaned = key.replace(/^\/+/, "");
  const version = getCdnVersion(cleaned);
  const suffix = version ? `?v=${encodeURIComponent(version)}` : "";
  return `${CDN_BASE}/${cleaned}${suffix}`;
}

/**
 * Extract the bucket key (e.g. `landing-page-previews/foo.webp`) from a full
 * CDN URL or a path. Strips query string and the `CDN_BASE` prefix when
 * present. Returns null if no recognizable key can be derived.
 */
export function extractCdnKey(urlOrKey: string): string | null {
  if (!urlOrKey) return null;
  const withoutQuery = urlOrKey.split("?")[0];
  if (withoutQuery.startsWith(CDN_BASE)) {
    return withoutQuery.slice(CDN_BASE.length).replace(/^\/+/, "");
  }
  // Accept bare keys ("folder/file.ext") as-is.
  if (/^(covers|guides|landing-page-previews|not-found)\//.test(withoutQuery)) {
    return withoutQuery;
  }
  return null;
}

